import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PurchaseNotifier } from '../components/PurchaseNotifier';
import { trackInitiateCheckout } from '../components/MetaPixel';
import { ArrowRight, CheckCircle2, ShieldCheck, Lock, HeartPulse, Gift } from 'lucide-react';
type Message = {
  id: string;
  sender: 'doc' | 'user' | 'card';
  text: string | React.ReactNode;
};
const DOC_AVATAR = 'https://cdn.magicpatterns.com/uploads/jHQ3ti2bdmP6k4iSE2nn2m/image.png';
export function LandingPageCanino7() {
  const [step, setStep] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatPhase, setChatPhase] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const checkoutUrl = 'https://pay.hotmart.com/Y104256235W?off=mstmo7g1&checkoutMode=10&bid=1773273486617';
  useEffect(() => {
    document.title = 'Diagnóstico de Alimentación | 30 Recetas Naturales';
  }, []);
  useEffect(() => {
    if (step === 1) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth'
        });
      }, showOptions ? 250 : 100);
    }
  }, [messages, isTyping, step, showOptions]);
  const addDocMessage = (id: string, text: string | React.ReactNode) => {
    setMessages(prev => [...prev, {
      id,
      sender: 'doc',
      text
    }]);
  };
  const addCardMessage = (id: string, content: React.ReactNode) => {
    setMessages(prev => [...prev, {
      id,
      sender: 'card',
      text: content
    }]);
  };
  const sendDocSequence = (msgs: {
    id: string;
    text: string | React.ReactNode;
    isCard?: boolean;
  }[], startDelay: number = 1500, onComplete?: () => void) => {
    const timeouts: NodeJS.Timeout[] = [];
    let cumulativeTime = startDelay;
    msgs.forEach((msg, i) => {
      const msgText = typeof msg.text === 'string' ? msg.text : '';
      const typingDuration = msg.isCard ? 300 : Math.min(2500 + msgText.length * 6, 4500);
      const typingTimeout = setTimeout(() => {
        if (!msg.isCard) setIsTyping(true);
      }, cumulativeTime);
      timeouts.push(typingTimeout);
      cumulativeTime += typingDuration;
      const msgTimeout = setTimeout(() => {
        setIsTyping(false);
        if (msg.isCard) {
          addCardMessage(msg.id, msg.text);
        } else {
          addDocMessage(msg.id, msg.text);
        }
        if (i === msgs.length - 1 && onComplete) {
          setTimeout(onComplete, 1500);
        }
      }, cumulativeTime);
      timeouts.push(msgTimeout);
      if (i < msgs.length - 1) {
        const readingPause = msg.isCard ? 4000 : Math.max(3000, Math.min(msgText.length * 20, 8000));
        cumulativeTime += readingPause;
      }
    });
    return timeouts;
  };
  useEffect(() => {
    if (step !== 1) return;
    let timeouts: NodeJS.Timeout[] = [];
    if (chatPhase === 0 && messages.length === 0) {
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-1', 'Hola 👋 Soy el Dr. Rennan Viegas. Voy a hacerte unas preguntas rápidas para analizar la alimentación de tu perro. Solo tomará 30 segundos.');
        const t2 = setTimeout(() => {
          setIsTyping(true);
        }, 1000);
        timeouts.push(t2);
        const t3 = setTimeout(() => {
          setIsTyping(false);
          addDocMessage('doc-2', 'Primero cuéntame, ¿qué edad tiene tu perro?');
          setShowOptions(true);
        }, 3500);
        timeouts.push(t3);
      }, 2500);
      timeouts.push(t1);
    }
    if (chatPhase === 1) {
      setShowOptions(false);
      setIsTyping(true);
      const t = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-3', 'Perfecto. Ahora dime, ¿qué tipo de comida consume normalmente?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t);
    }
    if (chatPhase === 2) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-4a', 'Entiendo. Muchos perros presentan problemas relacionados con su alimentación.');
        const t2 = setTimeout(() => {
          setIsTyping(true);
        }, 1000);
        timeouts.push(t2);
        const t3 = setTimeout(() => {
          setIsTyping(false);
          addDocMessage('doc-4b', '¿Tu perro tiene alguno de estos síntomas?');
          setShowOptions(true);
        }, 2800);
        timeouts.push(t3);
      }, 2000);
      timeouts.push(t1);
    }
    // Phase 3: Digestion question (NEW)
    if (chatPhase === 3) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-dig-1', 'Quiero entender mejor cómo está su digestión. ¿Has notado alguno de estos problemas?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t1);
    }
    // Phase 4: Energy question (NEW)
    if (chatPhase === 4) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-ener-1', 'Ahora dime algo importante sobre su energía diaria. ¿Cómo notas el nivel de energía de tu perro?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t1);
    }
    // Phase 5: Skin & coat question (NEW)
    if (chatPhase === 5) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-skin-1', 'La alimentación también afecta mucho la piel y el pelaje. ¿Has notado algo de esto?');
        setShowOptions(true);
      }, 2200);
      timeouts.push(t1);
    }
    // Phase 6: Analysis (was Phase 3)
    if (chatPhase === 6) {
      setShowOptions(false);
      setIsTyping(true);
      const t1 = setTimeout(() => {
        setIsTyping(false);
        addDocMessage('doc-5', 'Analizando la información sobre la alimentación de tu perro...');
        const t2 = setTimeout(() => {
          setIsTyping(true);
        }, 1500);
        timeouts.push(t2);
        const t3 = setTimeout(() => {
          setIsTyping(false);
          setChatPhase(7);
        }, 6500);
        timeouts.push(t3);
      }, 4000);
      timeouts.push(t1);
    }
    // Phase 7: Results + Emotional message (was Phase 4)
    if (chatPhase === 7) {
      const msgs = [{
        id: 'res-1',
        text: 'He terminado el análisis.'
      }, {
        id: 'res-2',
        text: 'Según tus respuestas, es posible que la alimentación actual de tu perro no esté cubriendo completamente sus necesidades nutricionales.'
      }, {
        id: 'res-3',
        text: 'Muchos perros que consumen alimentos poco balanceados comienzan a desarrollar problemas como alergias, problemas digestivos, caída de pelo o sobrepeso.'
      }, {
        id: 'res-4',
        text: 'Con el tiempo estos problemas empeoran y muchos dueños terminan gastando mucho dinero en veterinarios, medicamentos o tratamientos.'
      }, {
        id: 'res-5',
        text: 'Muchos dueños descubren demasiado tarde que la alimentación era una de las causas principales del deterioro en la salud de su perro.'
      }, {
        id: 'res-6',
        text: 'Déjame preguntarte algo importante… ¿Qué significa tu perro para ti?'
      }];
      const ts = sendDocSequence(msgs, 1500, () => {
        setShowOptions(true);
      });
      timeouts.push(...ts);
    }
    // Phase 8: Solution presentation (was Phase 5)
    if (chatPhase === 8) {
      setShowOptions(false);
      const productCard = <div className="bg-[#2F5D50] rounded-xl p-3 text-center" data-id="element-1809">
          <p className="text-[10px] font-bold text-green-300 uppercase tracking-wider mb-1" data-id="element-1810">
            Programa Clínico de Nutrición Natural para Perros
          </p>
          <div className="bg-white/10 rounded-lg p-2 mt-2 text-left" data-id="element-1811">
            <p className="text-white text-[11px] font-bold mb-1" data-id="element-1812">Incluye:</p>
            <ul className="text-white/90 text-[11px] space-y-1" data-id="element-1813">
              <li className="flex gap-1" data-id="element-1814">
                <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0 mt-0.5" data-id="element-1815" />{' '}
                30 recetas naturales balanceadas
              </li>
              <li className="flex gap-1" data-id="element-1816">
                <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0 mt-0.5" data-id="element-1817" />{' '}
                Recetas para cachorros, adultos y seniors
              </li>
              <li className="flex gap-1" data-id="element-1818">
                <CheckCircle2 className="w-3 h-3 text-green-300 flex-shrink-0 mt-0.5" data-id="element-1819" />{' '}
                Acceso inmediato y de por vida
              </li>
            </ul>
          </div>
        </div>;
      const bonosCard = <div className="bg-white border border-[#6DAA7F]/40 rounded-xl overflow-hidden" data-id="element-1820">
          <div className="bg-[#e8f5ec] px-3 py-2" data-id="element-1821">
            <p className="text-[11px] font-bold text-[#2F5D50] flex items-center gap-1" data-id="element-1822">
              <Gift className="w-3 h-3" data-id="element-1823" /> Regalos incluidos gratis:
            </p>
          </div>
          <div className="p-2.5 space-y-2" data-id="element-1824">
            <div className="flex items-start gap-2 bg-[#e8f5ec] p-2 rounded-lg" data-id="element-1825">
              <img src="https://cdn.magicpatterns.com/uploads/dhzCPguBEHAuB1vxaooPGU/ChatGPT_Image_5_feb_2026,_02_35_47_p.m..png" alt="Lista Negra + Lista Verde" className="w-10 h-10 object-cover rounded-md border border-[#6DAA7F] flex-shrink-0" data-id="element-1826" />
              <div data-id="element-1827">
                <p className="text-[11px] font-bold text-gray-900" data-id="element-1828">
                  REGALO #1: Lista Negra + Lista Verde
                </p>
                <p className="text-[10px] text-gray-600" data-id="element-1829">
                  Guía de alimentos seguros y prohibidos.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-[#FFF3E0] p-2 rounded-lg" data-id="element-1830">
              <img src="https://cdn.magicpatterns.com/uploads/tmJCjjYa4iD56VcWj6sEtX/ChatGPT_Image_5_feb_2026,_12_53_16_p.m..png" alt="Plan Express 7 Días" className="w-10 h-10 object-cover rounded-md border border-[#F57C00] flex-shrink-0" data-id="element-1831" />
              <div data-id="element-1832">
                <p className="text-[11px] font-bold text-gray-900" data-id="element-1833">
                  REGALO #2: Plan Express 7 Días
                </p>
                <p className="text-[10px] text-gray-600" data-id="element-1834">
                  Transición segura a comida casera.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-[#e8f5ec] p-2 rounded-lg" data-id="element-1835">
              <img src="https://cdn.magicpatterns.com/uploads/9pgdegNzbQ4opBiEgx4YKA/heylloyd-image-1769029546802.png" alt="Cómo Alargar la Vida de tu Perro" className="w-10 h-10 object-cover rounded-md border border-[#6DAA7F] flex-shrink-0" data-id="element-1836" />
              <div data-id="element-1837">
                <p className="text-[11px] font-bold text-gray-900" data-id="element-1838">
                  REGALO #3: Cómo Alargar la Vida de tu Perro
                </p>
                <p className="text-[10px] text-gray-600" data-id="element-1839">
                  Guía de hábitos diarios de salud.
                </p>
              </div>
            </div>
          </div>
        </div>;
      const sorteosContent = <div data-id="element-1840">
          <p className="font-bold text-[13px] mb-2" data-id="element-1841">🎁 Sorteos Especiales</p>
          <div className="space-y-1" data-id="element-1842">
            <p className="text-[13px]" data-id="element-1843">🎁 Cama para perro</p>
            <p className="text-[13px]" data-id="element-1844">🎁 Comedero para perro</p>
            <p className="text-[13px]" data-id="element-1845">🏥 Seguro veterinario SURA por 1 año</p>
          </div>
        </div>;
      const valueContent = <div data-id="element-1846">
          <p className="font-bold text-[13px] mb-2" data-id="element-1847">
            💰 Valor estimado real de todo este material:
          </p>
          <div className="space-y-1 text-[13px]" data-id="element-1848">
            <p data-id="element-1849">
              Consulta nutricional veterinaria:{' '}
              <span className="line-through text-gray-400" data-id="element-1850">$150.000</span>
            </p>
            <p data-id="element-1851">
              Plan alimenticio personalizado:{' '}
              <span className="line-through text-gray-400" data-id="element-1852">$300.000</span>
            </p>
            <p data-id="element-1853">
              Guías especializadas:{' '}
              <span className="line-through text-gray-400" data-id="element-1854">$150.000</span>
            </p>
            <div className="border-t border-dashed border-gray-300 pt-1.5 mt-1.5" data-id="element-1855">
              <p className="font-bold" data-id="element-1856">
                Valor total estimado:{' '}
                <span className="line-through text-gray-400" data-id="element-1857">$600.000+</span>
              </p>
            </div>
          </div>
        </div>;
      const msgs: {
        id: string;
        text: string | React.ReactNode;
        isCard?: boolean;
      }[] = [{
        id: 'sol-1',
        text: 'Eso es exactamente lo que sienten los dueños responsables.'
      }, {
        id: 'sol-2',
        text: 'La buena noticia es que mejorar la alimentación puede cambiar completamente la salud, energía y calidad de vida de un perro.'
      }, {
        id: 'sol-3',
        text: 'Durante los últimos 18 años he estudiado la nutrición canina y miles de dueños en Estados Unidos y Brasil han aplicado este método con resultados muy positivos.'
      }, {
        id: 'sol-4',
        text: 'Por eso creé un sistema simple para que cualquier dueño pueda preparar comida natural balanceada para su perro sin complicaciones.'
      }, {
        id: 'prod-card',
        text: productCard,
        isCard: true
      }, {
        id: 'sol-5',
        text: 'Además, hoy recibirás 3 regalos gratis para aplicar todo correctamente desde el primer día.'
      }, {
        id: 'bonos-card',
        text: bonosCard,
        isCard: true
      }, {
        id: 'sol-6',
        text: 'Y hoy también participas en sorteos especiales para dueños responsables.'
      }, {
        id: 'sorteos-msg',
        text: sorteosContent
      }, {
        id: 'value-msg',
        text: valueContent
      }, {
        id: 'sol-7',
        text: 'Pero solo por hoy puedes acceder al programa completo por solo $69.999 COP. Este precio es por tiempo limitado, mañana puede subir.'
      }, {
        id: 'sol-8',
        text: 'Si pudieras mejorar la alimentación y la salud de tu perro con una guía clara paso a paso… ¿te gustaría empezar hoy antes de que suba el precio?'
      }];
      const ts = sendDocSequence(msgs, 1500, () => {
        setShowOptions(true);
      });
      timeouts.push(...ts);
    }
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [step, chatPhase]);
  const handleOptionClick = (text: string) => {
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      sender: 'user',
      text
    }]);
    setChatPhase(prev => prev + 1);
  };
  const screenVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  return <main className="h-[100dvh] md:min-h-screen md:h-auto bg-[#F7F8F7] flex flex-col items-center md:justify-center md:p-4 font-sans text-gray-900 relative overflow-hidden" data-id="element-1858">
      <div className="fixed inset-0 z-0 opacity-[0.03] hidden md:block" style={{
      backgroundImage: 'radial-gradient(#2F5D50 0.5px, transparent 0.5px)',
      backgroundSize: '24px 24px'
    }} data-id="element-1859" />
      <div className="w-full max-w-md bg-white md:rounded-[2rem] md:shadow-xl overflow-hidden relative h-full md:h-auto md:min-h-[650px] md:max-h-[90vh] flex flex-col md:border md:border-gray-200/60 z-10" data-id="element-1860">
        <div className="flex-1 relative flex flex-col min-h-0" data-id="element-1861">
          <AnimatePresence mode="wait" data-id="element-1862">
            {step === 0 && <motion.div key="step0" variants={screenVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.3
          }} className="flex-1 flex flex-col items-center justify-center px-7 py-8 text-center relative overflow-hidden" style={{
            background: 'linear-gradient(165deg, #1a3a2a 0%, #2F5D50 35%, #3d7a68 65%, #2a6b5a 100%)'
          }} data-id="element-1863">
                <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 18c-2.5-4-7-6-7-10s3-8 7-8 7 4 7 8-4.5 6-7 10zm-12 6c-4 2.5-6 7-10 7s-8-3-8-7 4-7 8-7 6 4.5 10 7zm24 0c4 2.5 6 7 10 7s8-3 8-7-4-7-8-7-6 4.5-10 7zm-12 12c0 5 3 9 3 9s-3 4-3 4-3-4-3-4 3-4 3-9z' fill='%23ffffff' fill-opacity='1'/%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }} data-id="element-1864" />
                <motion.div className="absolute top-6 right-6 w-20 h-20 rounded-full" style={{
              background: 'radial-gradient(circle, rgba(255,200,50,0.12) 0%, transparent 70%)'
            }} animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-1865" />
                <motion.div className="absolute bottom-12 left-4 w-16 h-16 rounded-full" style={{
              background: 'radial-gradient(circle, rgba(109,170,127,0.2) 0%, transparent 70%)'
            }} animate={{
              scale: [1, 1.3, 1],
              opacity: [0.4, 0.7, 0.4]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} data-id="element-1866" />
                <div className="relative z-10 flex flex-col items-center" data-id="element-1867">
                  <div className="relative mb-5" data-id="element-1868">
                    <motion.div className="absolute -inset-2 rounded-full" style={{
                  background: 'conic-gradient(from 0deg, #F59E0B, #6DAA7F, #F59E0B, #6DAA7F, #F59E0B)'
                }} animate={{
                  rotate: 360
                }} transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-1869" />
                    <div className="relative w-36 h-36 rounded-full overflow-hidden border-[3px] border-white/90 shadow-xl" data-id="element-1870">
                      <img src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?q=80&w=400&auto=format&fit=crop" alt="Perro en evaluación nutricional" className="w-full h-full object-cover" data-id="element-1871" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-amber-400 to-amber-500 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white" data-id="element-1872">
                      <HeartPulse className="w-5 h-5" data-id="element-1873" />
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-2 rounded-full mb-5" data-id="element-1874">
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" data-id="element-1875" />
                    Evaluación Nutricional Canina
                  </span>
                  <h1 className="text-[2rem] font-extrabold text-white mb-3 leading-tight tracking-tight" data-id="element-1876">
                    ¿Tu perro come <span className="text-amber-300" data-id="element-1877">bien</span>?
                  </h1>
                  <p className="text-white/70 text-[15px] mb-7 leading-relaxed max-w-[300px]" data-id="element-1878">
                    Descúbrelo en 30 segundos con una evaluación rápida basada
                    en nutrición veterinaria.
                  </p>
                  <button onClick={() => setStep(1)} className="w-full text-white font-bold text-lg py-4.5 rounded-xl shadow-xl flex items-center justify-center gap-2 active:scale-[0.97] transition-transform" style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #F57C00 50%, #E65100 100%)',
                boxShadow: '0 6px 20px rgba(245,124,0,0.4), 0 2px 8px rgba(0,0,0,0.15)'
              }} data-id="element-1879">
                    Iniciar evaluación gratuita
                    <ArrowRight className="w-5 h-5" data-id="element-1880" />
                  </button>
                  <div className="mt-6 flex items-center justify-center gap-2.5" data-id="element-1881">
                    <div className="flex -space-x-2" data-id="element-1882">
                      {[11, 12, 13, 14, 15].map(i => <div key={i} className="w-7 h-7 rounded-full border-2 border-[#2F5D50] bg-gray-200 overflow-hidden shadow-sm" data-id="element-1883">
                          <img src={`https://i.pravatar.cc/100?img=${i}`} alt="Usuario" data-id="element-1884" />
                        </div>)}
                    </div>
                    <p className="text-[13px] text-white/70 font-medium" data-id="element-1885">
                      <strong className="text-amber-300" data-id="element-1886">+2.300</strong>{' '}
                      evaluaciones realizadas
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-4 text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-1887">
                    <div className="flex items-center gap-1" data-id="element-1888">
                      <ShieldCheck className="w-3.5 h-3.5" data-id="element-1889" /> Confidencial
                    </div>
                    <div className="flex items-center gap-1" data-id="element-1890">
                      <Lock className="w-3.5 h-3.5" data-id="element-1891" /> Seguro
                    </div>
                    <div className="flex items-center gap-1" data-id="element-1892">
                      <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1893" /> Gratuito
                    </div>
                  </div>
                </div>
              </motion.div>}
            {step === 1 && <motion.div key="step1" variants={screenVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.3
          }} className="flex-1 flex flex-col bg-[#f5f5f5] min-h-0" data-id="element-1894">
                <div className="bg-[#2F5D50] px-4 py-3 flex items-center gap-3 shadow-md z-10 flex-shrink-0" data-id="element-1895">
                  <div className="relative" data-id="element-1896">
                    <img src={DOC_AVATAR} alt="Dr. Rennan Viegas" className="w-10 h-10 rounded-full border-2 border-white/20 object-cover" data-id="element-1897" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-[#2F5D50] rounded-full" data-id="element-1898" />
                  </div>
                  <div data-id="element-1899">
                    <h2 className="text-white font-bold text-sm leading-tight" data-id="element-1900">
                      Dr. Rennan Viegas
                    </h2>
                    <p className="text-green-300 text-[10px] font-medium flex items-center gap-1" data-id="element-1901">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" data-id="element-1902" />
                      En línea
                    </p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3 min-h-0" style={{
              WebkitOverflowScrolling: 'touch'
            }} data-id="element-1903">
                  <div className="text-center mb-3" data-id="element-1904">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-200/50 px-2 py-1 rounded-full" data-id="element-1905">
                      Hoy
                    </span>
                  </div>
                  {messages.map((msg, index) => {
                const isDoc = msg.sender === 'doc';
                const isCard = msg.sender === 'card';
                if (isCard) {
                  return <motion.div key={msg.id} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="flex justify-start items-end gap-2" data-id="element-1906">
                          <div className="w-6 h-6 flex-shrink-0" data-id="element-1907" />
                          <div className="max-w-[85%]" data-id="element-1908">{msg.text}</div>
                        </motion.div>;
                }
                const showAvatar = isDoc && (index === 0 || messages[index - 1].sender !== 'doc');
                return <motion.div key={msg.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className={`flex ${isDoc ? 'justify-start' : 'justify-end'} items-end gap-2`} data-id="element-1909">
                        {isDoc && <div className="w-6 h-6 flex-shrink-0" data-id="element-1910">
                            {showAvatar && <img src={DOC_AVATAR} alt="Doc" className="w-full h-full rounded-full object-cover" data-id="element-1911" />}
                          </div>}
                        <div className={`max-w-[80%] p-3 text-[13px] leading-relaxed shadow-sm ${isDoc ? 'bg-white text-gray-800 rounded-2xl rounded-bl-sm border border-gray-100' : 'bg-[#2F5D50] text-white rounded-2xl rounded-br-sm'}`} data-id="element-1912">
                          {msg.text}
                        </div>
                      </motion.div>;
              })}
                  {isTyping && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex justify-start items-end gap-2" data-id="element-1913">
                      <div className="w-6 h-6 flex-shrink-0" data-id="element-1914">
                        <img src={DOC_AVATAR} alt="Doc" className="w-full h-full rounded-full object-cover" data-id="element-1915" />
                      </div>
                      <div className="bg-white p-3 rounded-2xl rounded-bl-sm border border-gray-100 shadow-sm flex items-center gap-1.5 h-10" data-id="element-1916">
                        <motion.div animate={{
                    y: [0, -4, 0]
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: 0
                  }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-1917" />
                        <motion.div animate={{
                    y: [0, -4, 0]
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: 0.15
                  }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-1918" />
                        <motion.div animate={{
                    y: [0, -4, 0]
                  }} transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: 0.3
                  }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-1919" />
                      </div>
                    </motion.div>}
                  <div ref={messagesEndRef} data-id="element-1920" />
                </div>
                {showOptions && !isTyping && [0, 1, 2, 3, 4, 5, 7, 8].includes(chatPhase) && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] flex-shrink-0" data-id="element-1921">
                      <p className="text-[10px] font-bold text-[#F57C00] uppercase tracking-wider mb-2 text-center animate-pulse" data-id="element-1922">
                        ⚡ Selecciona una respuesta para continuar
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center" data-id="element-1923">
                        {chatPhase === 0 && ['Cachorro (0-1 año)', 'Adulto (1-7 años)', 'Senior (+7 años)'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-1924">
                              {opt}
                            </button>)}
                        {chatPhase === 1 && ['Concentrado comercial', 'Comida casera', 'Mezcla de ambos'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-1925">
                              {opt}
                            </button>)}
                        {chatPhase === 2 && ['Alergias o picazón', 'Mal aliento', 'Caída excesiva de pelo', 'Sobrepeso', 'Ninguno'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-1926">
                              {opt}
                            </button>)}
                        {chatPhase === 3 && ['Diarrea frecuente', 'Gases o pancita inflamada', 'Estreñimiento', 'Digestión normal'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-1927">
                              {opt}
                            </button>)}
                        {chatPhase === 4 && ['Muy activo', 'Normal', 'Más cansado de lo normal', 'Duerme demasiado'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-1928">
                              {opt}
                            </button>)}
                        {chatPhase === 5 && ['Pelo opaco', 'Caída excesiva de pelo', 'Piel seca', 'Todo normal'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-full shadow-md hover:bg-[#245045] transition-colors active:scale-95" data-id="element-1929">
                              {opt}
                            </button>)}
                        {chatPhase === 7 && ['Es parte de mi familia', 'Es mi compañero de vida', 'Lo amo y quiero lo mejor para él'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="w-full px-5 py-3 bg-[#2F5D50] text-white text-sm font-bold rounded-xl shadow-md hover:bg-[#245045] transition-colors active:scale-95 text-left" data-id="element-1930">
                              {opt}
                            </button>)}
                        {chatPhase === 8 && ['Sí, quiero mejorar su alimentación', 'Quiero acceder al programa'].map(opt => <button key={opt} onClick={() => handleOptionClick(opt)} className="w-full px-5 py-3 bg-gradient-to-r from-[#F57C00] to-[#E65100] text-white text-sm font-black rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95 text-left" data-id="element-1931">
                              {opt}
                            </button>)}
                      </div>
                    </motion.div>}
                {chatPhase >= 9 && !isTyping && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] flex-shrink-0" data-id="element-1932">
                    <button onClick={() => setStep(2)} className="w-full bg-gradient-to-r from-[#F57C00] to-[#e07000] text-white font-black py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-base active:scale-95" data-id="element-1933">
                      VER PROGRAMA RECOMENDADO
                      <ArrowRight className="w-5 h-5" data-id="element-1934" />
                    </button>
                    <div className="flex items-center justify-center gap-3 mt-2.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1935">
                      <div className="flex items-center gap-1" data-id="element-1936">
                        <CheckCircle2 className="w-3 h-3" data-id="element-1937" /> Pago único
                      </div>
                      <div className="flex items-center gap-1" data-id="element-1938">
                        <Lock className="w-3 h-3" data-id="element-1939" /> Acceso inmediato
                      </div>
                      <div className="flex items-center gap-1" data-id="element-1940">
                        <ShieldCheck className="w-3 h-3" data-id="element-1941" /> Acceso de por vida
                      </div>
                    </div>
                  </motion.div>}
              </motion.div>}

            {/* SCREEN 2: PRODUCT PAGE */}
            {step === 2 && <motion.div key="step2" variants={screenVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.3
          }} className="flex-1 flex flex-col min-h-0 bg-white" data-id="element-1942">
                <div className="bg-[#2F5D50] px-4 py-3 text-center flex-shrink-0" data-id="element-1943">
                  <p className="text-[10px] font-bold text-green-300 uppercase tracking-wider" data-id="element-1944">
                    Tu programa recomendado
                  </p>
                  <p className="text-white font-extrabold text-sm mt-0.5" data-id="element-1945">
                    Programa Clínico de Nutrición Natural
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain" style={{
              WebkitOverflowScrolling: 'touch'
            }} data-id="element-1946">
                  <div className="p-4 space-y-4" data-id="element-1947">
                    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100" data-id="element-1948">
                      <img src="https://cdn.magicpatterns.com/uploads/gnEaoFwYpACgNWsD1np27d/ChatGPT_Image_10_mar_2026,_04_47_53_p.m..png" alt="Programa Clínico de Nutrición Natural - 30 Recetas Anti-Inflamación para Perros" className="w-full h-auto object-cover" data-id="element-1949" />
                    </div>
                    <div className="bg-[#2F5D50] rounded-xl p-4 text-white" data-id="element-1950">
                      <p className="text-[11px] font-bold text-green-300 uppercase tracking-wider mb-2" data-id="element-1951">
                        ¿Qué incluye el programa?
                      </p>
                      <ul className="space-y-2" data-id="element-1952">
                        {['30 recetas naturales balanceadas por veterinarios', 'Recetas para cachorros, adultos y seniors', 'Acceso inmediato y de por vida'].map((item, i) => <li key={i} className="flex items-start gap-2 text-[12px]" data-id="element-1953">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-300 flex-shrink-0 mt-0.5" data-id="element-1954" />
                            <span className="text-white/90" data-id="element-1955">{item}</span>
                          </li>)}
                      </ul>
                    </div>
                    <div className="bg-white border border-[#6DAA7F]/40 rounded-xl overflow-hidden" data-id="element-1956">
                      <div className="bg-[#e8f5ec] px-4 py-2.5" data-id="element-1957">
                        <p className="text-[12px] font-bold text-[#2F5D50] flex items-center gap-1.5" data-id="element-1958">
                          <Gift className="w-4 h-4" data-id="element-1959" /> 3 Regalos incluidos
                          GRATIS
                        </p>
                      </div>
                      <div className="p-3 space-y-2.5" data-id="element-1960">
                        <div className="flex items-start gap-3 bg-[#e8f5ec] p-2.5 rounded-lg border border-[#6DAA7F]/20" data-id="element-1961">
                          <img src="https://cdn.magicpatterns.com/uploads/dhzCPguBEHAuB1vxaooPGU/ChatGPT_Image_5_feb_2026,_02_35_47_p.m..png" alt="Lista Negra + Lista Verde" className="w-12 h-12 object-cover rounded-lg border border-[#6DAA7F] flex-shrink-0" data-id="element-1962" />
                          <div data-id="element-1963">
                            <p className="text-[12px] font-bold text-gray-900" data-id="element-1964">
                              REGALO #1: Lista Negra + Lista Verde
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-1965">
                              Guía de alimentos seguros y prohibidos para tu
                              perro.
                            </p>
                            <p className="text-[10px] font-semibold text-[#2F5D50] mt-0.5" data-id="element-1966">
                              Valor: $49.999 COP — Hoy: GRATIS
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-[#FFF3E0] p-2.5 rounded-lg border border-[#F57C00]/20" data-id="element-1967">
                          <img src="https://cdn.magicpatterns.com/uploads/tmJCjjYa4iD56VcWj6sEtX/ChatGPT_Image_5_feb_2026,_12_53_16_p.m..png" alt="Plan Express 7 Días" className="w-12 h-12 object-cover rounded-lg border border-[#F57C00] flex-shrink-0" data-id="element-1968" />
                          <div data-id="element-1969">
                            <p className="text-[12px] font-bold text-gray-900" data-id="element-1970">
                              REGALO #2: Plan Express 7 Días
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-1971">
                              Transición segura a comida casera sin problemas
                              digestivos.
                            </p>
                            <p className="text-[10px] font-semibold text-[#F57C00] mt-0.5" data-id="element-1972">
                              Valor: $49.999 COP — Hoy: GRATIS
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-[#e8f5ec] p-2.5 rounded-lg border border-[#6DAA7F]/20" data-id="element-1973">
                          <img src="https://cdn.magicpatterns.com/uploads/9pgdegNzbQ4opBiEgx4YKA/heylloyd-image-1769029546802.png" alt="Cómo Alargar la Vida de tu Perro" className="w-12 h-12 object-cover rounded-lg border border-[#6DAA7F] flex-shrink-0" data-id="element-1974" />
                          <div data-id="element-1975">
                            <p className="text-[12px] font-bold text-gray-900" data-id="element-1976">
                              REGALO #3: Cómo Alargar la Vida de tu Perro
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-1977">
                              Guía de hábitos diarios para mejorar su salud y
                              calidad de vida.
                            </p>
                            <p className="text-[10px] font-semibold text-[#2F5D50] mt-0.5" data-id="element-1978">
                              Valor: $49.999 COP — Hoy: GRATIS
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200/80 rounded-xl p-4" data-id="element-1979">
                      <p className="text-[12px] font-bold text-orange-800 mb-2 flex items-center gap-1.5" data-id="element-1980">
                        🎁 Sorteos Especiales (Solo compradores de hoy)
                      </p>
                      <div className="space-y-1.5" data-id="element-1981">
                        <p className="text-[12px] text-orange-700 flex items-center gap-2" data-id="element-1982">
                          🎁 Cama para perro
                        </p>
                        <p className="text-[12px] text-orange-700 flex items-center gap-2" data-id="element-1983">
                          🎁 Comedero para perro
                        </p>
                        <p className="text-[12px] text-orange-700 flex items-center gap-2" data-id="element-1984">
                          🏥 Seguro veterinario SURA por 1 año
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-1985">
                      <p className="text-[12px] font-bold text-gray-800 mb-2" data-id="element-1986">
                        💰 Valor real de todo el material:
                      </p>
                      <div className="space-y-1.5 text-[12px] text-gray-600" data-id="element-1987">
                        <div className="flex justify-between" data-id="element-1988">
                          <span data-id="element-1989">Consulta nutricional veterinaria</span>
                          <span className="line-through text-gray-400" data-id="element-1990">
                            $150.000
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-1991">
                          <span data-id="element-1992">Plan alimenticio personalizado</span>
                          <span className="line-through text-gray-400" data-id="element-1993">
                            $300.000
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-1994">
                          <span data-id="element-1995">Guías especializadas (3)</span>
                          <span className="line-through text-gray-400" data-id="element-1996">
                            $150.000
                          </span>
                        </div>
                        <div className="border-t border-dashed border-gray-300 pt-2 mt-2 flex justify-between font-bold text-gray-800" data-id="element-1997">
                          <span data-id="element-1998">Valor total estimado</span>
                          <span className="line-through text-gray-400" data-id="element-1999">
                            $600.000+
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl p-4 text-center border-2 border-[#F57C00] relative overflow-hidden" style={{
                  background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 50%, #FFCC80 100%)'
                }} data-id="element-2000">
                      <p className="text-[10px] font-bold text-[#E65100] uppercase tracking-wider mb-1" data-id="element-2001">
                        🔥 Acceso completo hoy
                      </p>
                      <p className="text-3xl font-black text-[#E65100]" data-id="element-2002">
                        $69.999 <span className="text-sm" data-id="element-2003">COP</span>
                      </p>
                      <p className="text-[11px] text-[#F57C00] font-bold mt-1" data-id="element-2004">
                        Pago único · Acceso de por vida
                      </p>
                      <p className="text-[10px] text-red-600 font-bold mt-1.5 animate-pulse" data-id="element-2005">
                        ⏰ Precio sube pronto a $99.999
                      </p>
                    </div>

                    {/* Video Testimonial */}
                    <div className="rounded-xl overflow-hidden border border-gray-200" data-id="element-2006">
                      <div className="bg-[#2F5D50] px-3 py-2" data-id="element-2007">
                        <p className="text-[11px] font-bold text-white text-center" data-id="element-2008">
                          🎥 Ella lo comprobó con su perro
                        </p>
                      </div>
                      <video controls preload="auto" playsInline className="w-full h-auto" style={{
                    maxHeight: '400px'
                  }} data-id="element-2009">
                        <source src="https://ydgkmdmvhrniztrhtrxq.supabase.co/storage/v1/object/sign/audio%20receta/freepik_1080x1920_30fps_14228.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80NDNkYTY1ZS1iYWM1LTQyYWQtODMzNS1iNWE4N2M0NDYxNmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyByZWNldGEvZnJlZXBpa18xMDgweDE5MjBfMzBmcHNfMTQyMjgubXA0IiwiaWF0IjoxNzcxNzY2MDY4LCJleHAiOjE4MDMzMDIwNjh9.jJthctnqJad8IknGrrF_q9s4zxF0FqSFHDuaUtaZTD8#t=0.1" type="video/mp4" data-id="element-2010" />
                      </video>
                    </div>

                    {/* Testimonials */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-2011">
                      <div className="bg-[#f0f2f5] px-3 py-2 border-b border-gray-200" data-id="element-2012">
                        <p className="text-[11px] font-bold text-gray-700 text-center" data-id="element-2013">
                          💬 Lo que dicen otros dueños
                        </p>
                      </div>
                      <div className="p-3 space-y-3 bg-white" data-id="element-2014">
                        {[{
                      name: 'María García',
                      avatar: 'https://i.pravatar.cc/150?img=5',
                      content: 'Luna se rascaba hasta sangrar. Después de 3 semanas con estas recetas, su piel ya no está roja y su pelaje brilla como cuando era cachorra.',
                      dog: 'Luna - Golden Retriever, 7 años',
                      likes: 47
                    }, {
                      name: 'Carlos Rodríguez',
                      avatar: 'https://i.pravatar.cc/150?img=12',
                      content: 'Max siempre tenía heces blandas y muchos gases. Con estas recetas se arregló su digestión en días y tiene una energía que no había visto en años.',
                      dog: 'Max - Labrador, 5 años',
                      likes: 89
                    }, {
                      name: 'Ana Patricia López',
                      avatar: 'https://i.pravatar.cc/150?img=9',
                      content: 'Coco se lamía las patas por las alergias. Empecé con la receta de pescado y fue un milagro. Ya no se lame y duerme tranquilo toda la noche.',
                      dog: 'Coco - Beagle, 4 años',
                      likes: 156
                    }].map((t, i) => <div key={i} className="flex gap-2" data-id="element-2015">
                            <img src={t.avatar} alt={t.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" data-id="element-2016" />
                            <div className="flex-1" data-id="element-2017">
                              <div className="bg-[#f0f2f5] rounded-2xl px-2.5 py-2" data-id="element-2018">
                                <p className="font-semibold text-[11px] text-gray-900" data-id="element-2019">
                                  {t.name}
                                </p>
                                <p className="text-[11px] text-gray-700 mt-0.5 leading-relaxed" data-id="element-2020">
                                  {t.content}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1 bg-white inline-block px-1.5 py-0.5 rounded-full" data-id="element-2021">
                                  🐕 {t.dog}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 ml-2 text-[10px] text-gray-400" data-id="element-2022">
                                <span data-id="element-2023">👍 {t.likes}</span>
                              </div>
                            </div>
                          </div>)}
                      </div>
                      <div className="bg-[#f0f2f5] px-3 py-2 text-center border-t border-gray-200" data-id="element-2024">
                        <p className="text-[10px] text-gray-500" data-id="element-2025">
                          💬 Más de{' '}
                          <span className="font-bold text-gray-700" data-id="element-2026">
                            1,200 comentarios positivos
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Dr. Rennan Viegas Section */}
                    <div className="rounded-xl border border-[#6DAA7F]/40 overflow-hidden bg-white" data-id="element-2027">
                      <div className="bg-[#2F5D50] px-3 py-2" data-id="element-2028">
                        <p className="text-[11px] font-bold text-white text-center" data-id="element-2029">
                          🩺 Respaldo científico
                        </p>
                      </div>
                      <div className="p-4" data-id="element-2030">
                        <div className="rounded-lg overflow-hidden border border-gray-200 mb-4 shadow-sm" data-id="element-2031">
                          <img src="https://cdn.magicpatterns.com/uploads/wAzhSsfdxojeVHqwzkSjv6/image.png" alt="Perfil de Instagram del Dr. Rennan Viegas - 635 mil seguidores" className="w-full h-auto object-cover" data-id="element-2032" />
                        </div>
                        <div className="flex items-center gap-3 mb-3" data-id="element-2033">
                          <img src={DOC_AVATAR} alt="Dr. Rennan Viegas" className="w-12 h-12 rounded-full border-2 border-[#6DAA7F] object-cover flex-shrink-0" data-id="element-2034" />
                          <div data-id="element-2035">
                            <p className="text-[13px] font-bold text-[#2F5D50]" data-id="element-2036">
                              Dr. Rennan Viegas
                            </p>
                            <p className="text-[11px] text-[#6DAA7F] font-semibold" data-id="element-2037">
                              Médico Veterinario · +18 años
                            </p>
                          </div>
                        </div>
                        <p className="text-[12px] text-gray-700 leading-relaxed mb-3" data-id="element-2038">
                          Protocolo inspirado en la metodología del{' '}
                          <span className="font-bold text-[#2F5D50]" data-id="element-2039">
                            Dr. Rennan Viegas
                          </span>
                          , médico veterinario con más de 18 años de experiencia
                          en nutrición preventiva.
                        </p>
                        <p className="text-[11px] text-gray-500 leading-relaxed mb-3" data-id="element-2040">
                          Su enfoque ha ayudado a miles de dueños a mejorar la
                          salud digestiva de sus mascotas.
                        </p>
                        <div className="grid grid-cols-3 gap-2 bg-[#e8f5ec] rounded-lg p-3" data-id="element-2041">
                          <div className="text-center" data-id="element-2042">
                            <p className="text-[15px] font-black text-[#2F5D50]" data-id="element-2043">
                              +18
                            </p>
                            <p className="text-[9px] text-gray-500" data-id="element-2044">
                              años exp.
                            </p>
                          </div>
                          <div className="text-center border-x border-[#6DAA7F]/30" data-id="element-2045">
                            <p className="text-[15px] font-black text-[#2F5D50]" data-id="element-2046">
                              +635K
                            </p>
                            <p className="text-[9px] text-gray-500" data-id="element-2047">
                              seguidores
                            </p>
                          </div>
                          <div className="text-center" data-id="element-2048">
                            <p className="text-[15px] font-black text-[#2F5D50]" data-id="element-2049">
                              Miles
                            </p>
                            <p className="text-[9px] text-gray-500" data-id="element-2050">
                              pets ayudadas
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] flex-shrink-0" data-id="element-2051">
                  <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full" onClick={() => trackInitiateCheckout()} data-id="element-2052">
                    <button className="w-full bg-gradient-to-r from-[#F57C00] to-[#E65100] text-white font-black py-4 rounded-xl shadow-lg hover:shadow-xl transition-all flex flex-col items-center justify-center gap-0.5 text-base active:scale-95 animate-pulse" data-id="element-2053">
                      <span data-id="element-2054">🔥 ACCEDER AHORA — CUPOS LIMITADOS</span>
                      <span className="text-[11px] font-bold opacity-90" data-id="element-2055">
                        por $69.999 COP · Precio sube pronto a $99.999
                      </span>
                    </button>
                  </a>
                  <p className="text-[10px] text-red-500 font-bold text-center mt-2 animate-pulse" data-id="element-2056">
                    ⏰ Oferta válida solo por hoy — Quedan pocos cupos
                  </p>
                  <div className="flex items-center justify-center gap-3 mt-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2057">
                    <div className="flex items-center gap-1" data-id="element-2058">
                      <CheckCircle2 className="w-3 h-3" data-id="element-2059" /> Pago único
                    </div>
                    <div className="flex items-center gap-1" data-id="element-2060">
                      <Lock className="w-3 h-3" data-id="element-2061" /> Pago seguro
                    </div>
                    <div className="flex items-center gap-1" data-id="element-2062">
                      <ShieldCheck className="w-3 h-3" data-id="element-2063" /> Garantía
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
      <PurchaseNotifier enabled={step === 2} displayPrice="$69.999 COP" data-id="element-2064" />
    </main>;
}